import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CarSimple, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Suitcase,
  ArrowRight,
  CheckCircle,
  Phone,
  Airplane,
  WhatsappLogo,
  CaretDown,
  Envelope,
  AirplaneLanding,
  Warning,
  ShieldCheck,
  CurrencyGbp,
  Headset,
  Medal,
  Globe,
  Lightning,
  Buildings,
  Taxi
} from '@phosphor-icons/react';
import axios from 'axios';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { CurrencySelector } from '../components/CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';
import { trackEvent } from '../utils/analytics';
import { captureReferral } from '../utils/referral';
import { EXTRAS, ExtraPanel } from '../components/TravelExtras';
import { DESTINATIONS, ROUTES } from '../data/seoData';
import { SEOHead } from '../components/SEOHead';
import { CONTACT_EMAIL, WHATSAPP_URL, WHATSAPP_DISPLAY } from '../config/contact';
import { TrustpilotLink } from '../components/Trustpilot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Search box tabs: the transfer search plus the cross-sell widgets.
const PRODUCT_TABS = [
  { id: 'transfers', label: 'Transfers', icon: Taxi },
  ...['cars', 'tours', 'esim'].map(id => ({ id, label: EXTRAS[id].tab, icon: EXTRAS[id].icon })),
];

const HERO_COPY = {
  transfers: { title: 'Are you looking for airport transfers?', sub: 'You have come to the right place' },
  cars:      { title: 'Rent a car for your trip', sub: 'Compare rental cars from trusted suppliers worldwide' },
  tours:     { title: 'Tours & attraction tickets', sub: 'Skip the lines at the top sights of your destination' },
  esim:      { title: 'Stay connected abroad', sub: 'Mobile data eSIMs for 200+ countries, no roaming fees' },
};

// Hero Images
const HERO_IMAGE = "/images/hero.jpg";

// Fleet Images — stable production sources
const FLEET_IMAGES = {
  standard:  "https://images.pexels.com/photos/6191762/pexels-photo-6191762.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  executive: "https://images.unsplash.com/photo-1592222269733-1d44ea5ab43b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  minivan:   "https://images.pexels.com/photos/17455625/pexels-photo-17455625.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  minibus:   "https://images.pexels.com/photos/19871521/pexels-photo-19871521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

export default function HomePage() {
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();
  const initialTab = PRODUCT_TABS.some(t => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'transfers';
  const [product, setProduct] = useState(initialTab);
  // Widgets load heavy scripts, so each one mounts on first visit and then stays mounted.
  const [openedTabs, setOpenedTabs] = useState(() => new Set([initialTab]));
  const selectProduct = (id) => {
    setProduct(id);
    setOpenedTabs(prev => new Set(prev).add(id));
    if (id !== 'transfers') trackEvent('cross_sell_opened', { kind: id, placement: 'home_tabs' });
  };
  const [tripType, setTripType] = useState('one-way');
  // Route pages link here with ?from=&to= so the visitor only picks date and time.
  const [formData, setFormData] = useState({
    pickup_location: searchParams.get('from') || '',
    dropoff_location: searchParams.get('to') || '',
    pickup_date: '',
    pickup_time: '09:00',
    return_date: '',
    return_time: '09:00',
    passengers: 2,
    luggage: 2
  });
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [timeError, setTimeError] = useState('');

  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    // Capture referral code from URL ?ref= param
    captureReferral();
  }, []);

  // Validate 24-hour rule
  const validatePickupTime = (date, time) => {
    if (!date || !time) return true; // Don't show error if fields are empty
    
    const now = new Date();
    const pickupDateTime = new Date(`${date}T${time}`);
    const minPickupTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
    
    if (pickupDateTime < minPickupTime) {
      setTimeError('Pick-up must be at least 24 hours in advance.');
      return false;
    }
    
    setTimeError('');
    return true;
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    
    // Validate time when date or time changes
    if (name === 'pickup_date' || name === 'pickup_time') {
      validatePickupTime(
        name === 'pickup_date' ? value : formData.pickup_date,
        name === 'pickup_time' ? value : formData.pickup_time
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validatePickupTime(formData.pickup_date, formData.pickup_time)) return;

    trackEvent('search_started', {
      pickup: formData.pickup_location,
      dropoff: formData.dropoff_location,
      date: formData.pickup_date,
      passengers: formData.passengers,
      trip_type: tripType,
    });

    if (tripType === 'round-trip') {
      // Round-trip → pre-fill and open the quote form
      const params = new URLSearchParams({
        trip_type: 'round-trip',
        from: formData.pickup_location,
        to: formData.dropoff_location,
        date: formData.pickup_date,
        time: formData.pickup_time,
        passengers: formData.passengers,
        luggage: formData.luggage,
        return_date: formData.return_date || '',
        return_time: formData.return_time || '',
      });
      navigate(`/quote?${params.toString()}`);
      return;
    }

    // One-way → live vehicle search
    navigate('/results', { state: { ...formData, trip_type: tripType, currency } });
  };

  const isFormValid = () => {
    if (!formData.pickup_location || !formData.dropoff_location ||
        !formData.pickup_date || !formData.pickup_time || timeError) return false;
    if (tripType === 'round-trip' && (!formData.return_date || !formData.return_time)) return false;
    return true;
  };

  const features = [
    { icon: <MapPin size={32} weight="light" />, title: "Door to Door Service", desc: "From the airport directly to your destination" },
    { icon: <CarSimple size={32} weight="light" />, title: "Private Transfers", desc: "Exclusive service, no shared rides" },
    { icon: <Users size={32} weight="light" />, title: "Meet & Greet", desc: "Driver meets you at arrivals with name sign" },
    { icon: <Airplane size={32} weight="light" />, title: "Flight Tracking", desc: "We monitor your flight for delays" },
  ];

  const fleet = [
    { id: "standard",  name: "Standard",       desc: "Reliable everyday saloons for comfortable transfers",     passengers: 3,  luggage: 2,  image: FLEET_IMAGES.standard,  alt: "Standard airport transfer — Toyota Prius or similar" },
    { id: "executive", name: "Executive",       desc: "Premium business saloons for a professional journey",     passengers: 4,  luggage: 3,  image: FLEET_IMAGES.executive, alt: "Executive airport transfer — Mercedes E-Class or similar" },
    { id: "minivan",   name: "Minivan / MPV",   desc: "Spacious people carriers perfect for families with luggage", passengers: 7, luggage: 5, image: FLEET_IMAGES.minivan,   alt: "Minivan airport transfer — Mercedes Vito or similar" },
    { id: "minibus",   name: "Minibus",         desc: "Comfortable minibuses for larger groups and events",      passengers: 16, luggage: 16, image: FLEET_IMAGES.minibus,   alt: "Minibus airport transfer — Mercedes Sprinter or similar" },
  ];

  const steps = [
    { num: "01", title: "Fill the Booking Form", desc: "Enter your pickup and dropoff locations, date, time, and passenger details." },
    { num: "02", title: "Select Your Vehicle", desc: "Choose from our fleet of economy, business, or group vehicles." },
    { num: "03", title: "Complete Payment", desc: "Pay securely online with card via Stripe. Instant confirmation." },
    { num: "04", title: "Enjoy Your Ride", desc: "Your driver will be ready at your pickup location on time!" },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Private Airport Transfers Worldwide | Planet Transfers"
        description="Book reliable private airport transfers worldwide. Professional drivers, fixed prices, and comfortable vehicles for airport, hotel, and private address transfers."
        canonical="/"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CarSimple size={32} weight="fill" className="text-[#1a3a6e]" />
            <span className="font-['Playfair_Display'] text-xl font-semibold text-slate-900">Planet Transfers</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <CurrencySelector />
            </div>
            {/* WhatsApp — header */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-800 transition-colors"
              data-testid="nav-whatsapp"
            >
              <WhatsappLogo size={20} weight="fill" className="text-green-500" />
              <span>{WHATSAPP_DISPLAY}</span>
            </a>
            <a 
              href="/book"
              className="btn-gold py-2.5 px-4 sm:px-5 flex items-center gap-2 text-sm"
              data-testid="book-now-btn"
            >
              Book Now
            </a>
            <button 
              onClick={() => {
                const params = {};
                if (formData.pickup_location)  params.from = formData.pickup_location;
                if (formData.dropoff_location) params.to   = formData.dropoff_location;
                const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
                window.open(`/quote${qs}`, '_blank', 'width=800,height=900,scrollbars=yes');
              }}
              className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#1a3a6e] border-2 border-[#1a3a6e] hover:bg-[#1a3a6e] hover:text-white transition-all px-4 sm:px-5 py-2.5 rounded-lg"
              data-testid="request-quote-btn"
            >
              Request Quote
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section — search-first layout */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '520px',
        }}
        data-testid="hero-section"
      >
        {/* Dark overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,19,43,0.72) 0%, rgba(11,19,43,0.55) 60%, rgba(11,19,43,0.80) 100%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6" style={{ paddingTop: '90px', paddingBottom: '60px' }}>
          {/* Heading — compact, above search bar */}
          <div className="text-center mb-6 fade-in">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Playfair_Display'] font-semibold text-white leading-tight mb-2">
              {HERO_COPY[product].title}
            </h1>
            <p className="text-base sm:text-lg text-white/80 font-light">{HERO_COPY[product].sub}</p>
          </div>

          {/* Product tabs */}
          <div className="grid grid-cols-4 sm:flex gap-1" role="tablist" data-testid="product-tabs">
            {PRODUCT_TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={product === id}
                onClick={() => selectProduct(id)}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 sm:px-5 py-2 sm:py-2.5 rounded-t-xl text-[11px] sm:text-sm font-semibold whitespace-nowrap transition-colors ${
                  product === id ? 'bg-white text-[#1a3a6e]' : 'bg-white/15 text-white hover:bg-white/25'
                }`}
                data-testid={`product-tab-${id}`}
              >
                <Icon size={18} weight={product === id ? 'fill' : 'regular'} />
                {label}
              </button>
            ))}
          </div>

          {/* ── Search Widget ─────────────────────────────────────────── */}
          <div
            id="booking-form"
            data-testid="booking-widget"
            className={`bg-white rounded-2xl shadow-2xl overflow-visible ${product === PRODUCT_TABS[0].id ? "rounded-tl-none" : ""}`}
            style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.28)' }}
          >
            {/* Cross-sell widgets */}
            {['cars', 'tours', 'esim'].map(id => openedTabs.has(id) && (
              <div key={id} className={product === id ? 'p-4 sm:p-6' : 'hidden'} data-testid={`product-panel-${id}`}>
                <ExtraPanel kind={id} currency={currency} />
              </div>
            ))}

            <div className={product === 'transfers' ? '' : 'hidden'}>
            {/* Top bar: trip type + passengers */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 pt-4 pb-3 border-b border-slate-100">
              {/* Trip type toggle */}
              <div className="flex bg-slate-100 rounded-lg p-1 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setTripType('one-way')}
                  className={`flex-1 sm:flex-none px-5 py-2 text-sm font-semibold rounded-md transition-all ${tripType === 'one-way' ? 'bg-[#1a3a6e] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="trip-type-oneway"
                >
                  One-way
                </button>
                <button
                  type="button"
                  onClick={() => setTripType('round-trip')}
                  className={`flex-1 sm:flex-none px-5 py-2 text-sm font-semibold rounded-md transition-all ${tripType === 'round-trip' ? 'bg-[#1a3a6e] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="trip-type-roundtrip"
                >
                  Round-trip
                </button>
              </div>

              {/* Passengers & Luggage */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPassengerDropdown(!showPassengerDropdown)}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-slate-300 bg-white w-full sm:w-auto"
                  data-testid="passengers-luggage-btn"
                >
                  <Users size={16} className="text-slate-400" />
                  <span>{formData.passengers} Adult{formData.passengers > 1 ? 's' : ''}</span>
                  <Suitcase size={14} className="text-slate-400 ml-1" />
                  <span>{formData.luggage} bag{formData.luggage !== 1 ? 's' : ''}</span>
                  <CaretDown size={14} className="text-slate-400 ml-1" />
                </button>
                {showPassengerDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowPassengerDropdown(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl p-4 z-20 min-w-[220px]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-700">Passengers</span>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setFormData({...formData, passengers: Math.max(1, formData.passengers - 1)})} className="w-8 h-8 border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold">−</button>
                          <span className="w-6 text-center text-sm font-semibold">{formData.passengers}</span>
                          <button type="button" onClick={() => setFormData({...formData, passengers: Math.min(50, formData.passengers + 1)})} className="w-8 h-8 border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold">+</button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Luggage</span>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setFormData({...formData, luggage: Math.max(0, formData.luggage - 1)})} className="w-8 h-8 border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold">−</button>
                          <span className="w-6 text-center text-sm font-semibold">{formData.luggage}</span>
                          <button type="button" onClick={() => setFormData({...formData, luggage: Math.min(50, formData.luggage + 1)})} className="w-8 h-8 border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold">+</button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Search fields row */}
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col lg:flex-row lg:items-stretch divide-y lg:divide-y-0 lg:divide-x divide-slate-100">

                {/* From */}
                <div className="flex-1 min-w-0 px-4 sm:px-5 py-3 lg:py-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <AirplaneLanding size={13} className="text-[#1a3a6e]" /> From
                  </label>
                  <AddressAutocomplete
                    name="pickup_location"
                    value={formData.pickup_location}
                    onChange={handleInputChange}
                    placeholder="Airport, hotel or address..."
                    required
                    dataTestId="pickup-location"
                  />
                </div>

                {/* To */}
                <div className="flex-1 min-w-0 px-4 sm:px-5 py-3 lg:py-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <MapPin size={13} className="text-[#1a3a6e]" /> To
                  </label>
                  <AddressAutocomplete
                    name="dropoff_location"
                    value={formData.dropoff_location}
                    onChange={handleInputChange}
                    placeholder="Airport, hotel or address..."
                    required
                    dataTestId="dropoff-location"
                  />
                </div>

                {/* Pickup Date */}
                <div className="px-4 sm:px-5 py-3 lg:py-4 lg:w-44">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Calendar size={13} className="text-[#1a3a6e]" /> Date
                  </label>
                  <input
                    type="date"
                    name="pickup_date"
                    value={formData.pickup_date}
                    onChange={handleInputChange}
                    className="w-full text-sm text-slate-800 bg-transparent border-none outline-none font-medium placeholder-slate-400 cursor-pointer"
                    required
                    min={getMinDate()}
                    data-testid="pickup-date"
                  />
                </div>

                {/* Pickup Time */}
                <div className="px-4 sm:px-5 py-3 lg:py-4 lg:w-32">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Clock size={13} className="text-[#1a3a6e]" /> Time
                  </label>
                  <div className="flex items-center gap-1" data-testid="pickup-time">
                    <select
                      value={(formData.pickup_time || '09:00').split(':')[0] || '09'}
                      onChange={e => {
                        const mins = (formData.pickup_time || '09:00').split(':')[1] || '00';
                        handleInputChange({ target: { name: 'pickup_time', value: `${e.target.value}:${mins}` } });
                      }}
                      className="bg-transparent border-none outline-none text-sm text-slate-800 font-medium cursor-pointer"
                    >
                      {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span className="text-slate-400 font-medium text-sm">:</span>
                    <select
                      value={(formData.pickup_time || '09:00').split(':')[1] || '00'}
                      onChange={e => {
                        const hrs = (formData.pickup_time || '09:00').split(':')[0] || '09';
                        handleInputChange({ target: { name: 'pickup_time', value: `${hrs}:${e.target.value}` } });
                      }}
                      className="bg-transparent border-none outline-none text-sm text-slate-800 font-medium cursor-pointer"
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Return Date + Time — only when round-trip */}
                {tripType === 'round-trip' && (
                  <>
                    <div className="px-4 sm:px-5 py-3 lg:py-4 lg:w-44">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                        <Calendar size={13} className="text-[#1a3a6e]" /> Return date
                      </label>
                      <input
                        type="date"
                        name="return_date"
                        value={formData.return_date}
                        onChange={handleInputChange}
                        className="w-full text-sm text-slate-800 bg-transparent border-none outline-none font-medium placeholder-slate-400 cursor-pointer"
                        required
                        min={formData.pickup_date || getMinDate()}
                        data-testid="return-date"
                      />
                    </div>
                    <div className="px-4 sm:px-5 py-3 lg:py-4 lg:w-32">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                        <Clock size={13} className="text-[#1a3a6e]" /> Time
                      </label>
                      <div className="flex items-center gap-1" data-testid="return-time">
                        <select
                          value={(formData.return_time || '09:00').split(':')[0] || '09'}
                          onChange={e => {
                            const mins = (formData.return_time || '09:00').split(':')[1] || '00';
                            handleInputChange({ target: { name: 'return_time', value: `${e.target.value}:${mins}` } });
                          }}
                          className="bg-transparent border-none outline-none text-sm text-slate-800 font-medium cursor-pointer"
                        >
                          {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <span className="text-slate-400 font-medium text-sm">:</span>
                        <select
                          value={(formData.return_time || '09:00').split(':')[1] || '00'}
                          onChange={e => {
                            const hrs = (formData.return_time || '09:00').split(':')[0] || '09';
                            handleInputChange({ target: { name: 'return_time', value: `${hrs}:${e.target.value}` } });
                          }}
                          className="bg-transparent border-none outline-none text-sm text-slate-800 font-medium cursor-pointer"
                        >
                          {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {/* Search button */}
                <div className="px-4 sm:px-5 py-3 lg:py-4 lg:w-auto flex items-end">
                  <button
                    type="submit"
                    className="btn-gold w-full lg:w-auto px-8 py-3 flex items-center justify-center gap-2 font-semibold text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="continue-btn"
                    disabled={!isFormValid()}
                  >
                    {tripType === 'round-trip' ? 'Get Quote' : 'Search'}
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              {/* Time error / sub-bar */}
              {(timeError || tripType === 'round-trip') && (
                <div className="px-4 sm:px-6 pb-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  {timeError && (
                    <div className="flex items-center gap-2 text-red-600 text-xs" data-testid="time-error">
                      <Warning size={14} weight="fill" />
                      {timeError}
                    </div>
                  )}
                  {tripType === 'round-trip' && (
                    <p className="text-xs text-slate-400 sm:ml-auto">Round-trip bookings are handled via a personalised quote.</p>
                  )}
                </div>
              )}
            </form>

            {/* Quote CTA */}
            <div className="text-center py-3 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  const params = {};
                  if (formData.pickup_location)  params.from = formData.pickup_location;
                  if (formData.dropoff_location) params.to   = formData.dropoff_location;
                  const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
                  window.open(`/quote${qs}`, '_blank', 'width=800,height=900,scrollbars=yes');
                }}
                className="text-xs font-semibold text-[#1a3a6e] hover:underline"
                data-testid="quote-cta-below-form"
              >
                Need a return or custom route? Request a quote →
              </button>
            </div>
            </div>
          </div>

          {/* Trust badges below search bar */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-6 fade-in">
            {[
              { icon: <CheckCircle size={16} weight="fill" className="text-green-400" />, text: "Free Cancellation" },
              { icon: <ShieldCheck size={16} weight="fill" className="text-amber-400" />, text: "Licensed & Insured" },
              { icon: <Users size={16} weight="fill" className="text-blue-400" />, text: "Meet & Greet" },
              { icon: <Headset size={16} weight="fill" className="text-purple-400" />, text: "24/7 Support" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-1.5 text-white/85 text-xs font-medium">
                {b.icon} {b.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Why Choose Planet Transfers?</h2>
            <p className="text-lg text-slate-600">Premium service with unmatched reliability</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="feature-card fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="text-[#1a3a6e] mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Our Fleet</h2>
            <p className="text-lg text-slate-600">Choose the perfect vehicle for your journey</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {fleet.map((vehicle, idx) => (
              <div key={vehicle.id} className="fleet-card fade-in" style={{ animationDelay: `${idx * 0.1}s` }} data-testid={`fleet-card-${vehicle.id}`}>
                <div className="overflow-hidden">
                  <img 
                    src={vehicle.image} 
                    alt={vehicle.alt}
                    loading="lazy"
                    width="400"
                    height="267"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-1">{vehicle.name}</h3>
                  <p className="text-slate-600 text-sm mb-3">{vehicle.desc}</p>
                  <p className="text-xs text-slate-400 italic mb-4">
                    {vehicle.id === 'standard'  && 'Toyota Prius, VW Passat or similar vehicle'}
                    {vehicle.id === 'executive' && 'Mercedes E-Class, BMW 5 Series or similar vehicle'}
                    {vehicle.id === 'minivan'   && 'Mercedes Vito, VW Transporter or similar vehicle'}
                    {vehicle.id === 'minibus'   && 'Mercedes Sprinter, Ford Transit or similar vehicle'}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users size={16} /> Up to {vehicle.passengers}
                    </span>
                    <span className="flex items-center gap-1">
                      <Suitcase size={16} /> {vehicle.luggage} bags
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section - SEO Task 7 */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Why Travellers Choose Us</h2>
            <p className="text-slate-400">Reliable, professional airport transfers worldwide</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a3a6e]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Medal size={32} className="text-[#1a3a6e]" />
              </div>
              <p className="font-medium">Best Price Guarantee</p>
              <p className="text-sm text-slate-400 mt-1">Competitive rates always</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a3a6e]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={32} className="text-[#1a3a6e]" />
              </div>
              <p className="font-medium">Professional Vetted Drivers</p>
              <p className="text-sm text-slate-400 mt-1">Licensed & insured</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a3a6e]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CurrencyGbp size={32} className="text-[#1a3a6e]" />
              </div>
              <p className="font-medium">No Hidden Costs</p>
              <p className="text-sm text-slate-400 mt-1">Transparent pricing</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a3a6e]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe size={32} className="text-[#1a3a6e]" />
              </div>
              <p className="font-medium">Global Airport Coverage</p>
              <p className="text-sm text-slate-400 mt-1">500+ airports worldwide</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1a3a6e]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightning size={32} className="text-[#1a3a6e]" />
              </div>
              <p className="font-medium">Fast & Reliable Service</p>
              <p className="text-sm text-slate-400 mt-1">On-time guarantee</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Simple steps to book your transfer</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="text-5xl font-['Playfair_Display'] text-[#1a3a6e] mb-4">{step.num}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Service Areas</h2>
            <p className="text-lg text-slate-600">Popular destinations, plus 500+ more airports worldwide</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {DESTINATIONS.map((d, idx) => (
              <a
                key={d.slug}
                href={`/airport-transfer/${d.slug}`}
                className="location-card fade-in block"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`location-${d.slug}`}
              >
                <img src={d.heroImage} alt={`${d.city} airport transfer`} loading="lazy" />
                <div className="overlay">
                  <h3 className="text-2xl font-semibold">{d.city}</h3>
                  <p className="text-sm text-white/80">{d.airport}</p>
                  <p className="text-sm text-white/60 mt-1">{d.country}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA — small, below customer sections */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#1a1a2e] rounded-full flex items-center justify-center flex-shrink-0">
              <Buildings size={22} className="text-[#1a3a6e]" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-base">Travel agency or hotel?</p>
              <p className="text-sm text-slate-500">Partner with Planet Transfers — earn commission on every referred booking.</p>
            </div>
          </div>
          <a
            href="/partners"
            className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-[#1a1a2e] border-2 border-[#1a1a2e] hover:bg-[#1a1a2e] hover:text-white transition-all px-5 py-2.5 rounded-lg whitespace-nowrap"
            data-testid="partner-cta-homepage"
          >
            Partner Programme
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <CarSimple size={32} weight="fill" className="text-[#1a3a6e]" />
                <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#1a3a6e]">Planet Transfers</span>
              </div>
              <p className="text-white/70">
                Premium airport transfer service operating worldwide. Reliable, professional, and always on time.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#fleet">Our Fleet</a></li>
                <li><a href="#locations">Destinations</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="/partners" className="text-[#1a3a6e] hover:text-[#1a3a6e]/80">Partner Programme</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Popular Routes</h4>
              <ul className="space-y-2">
                {ROUTES.slice(0, 8).map(r => (
                  <li key={r.slug}><a href={`/transfer/${r.slug}`}>{r.from} → {r.to}</a></li>
                ))}
                <li><a href="/transfers" className="text-[#1a3a6e] hover:text-[#1a3a6e]/80">All {ROUTES.length} transfer routes</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/terms-and-conditions">Terms &amp; Conditions</a></li>
                <li><a href="/privacy-policy">Privacy Policy</a></li>
                <li><a href="/cookie-policy">Cookie Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <WhatsappLogo size={18} weight="fill" className="text-green-400" />
                  <a
                    href={`${WHATSAPP_URL}?text=Hi%2C%20I%27d%20like%20help%20with%20a%20transfer%20booking`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >WhatsApp</a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={18} />
                  <span>{WHATSAPP_DISPLAY}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Envelope size={18} />
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </li>
                <li>
                  <TrustpilotLink />
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/50 text-sm">
            <p>&copy; {new Date().getFullYear()} Planet Transfers. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <WhatsappLogo size={28} weight="fill" />
      </a>
    </div>
  );
}
